import { Typography } from '@mui/material';
import Button from 'components/Button';
import { AppAutocomplete } from 'components/FormElements';
import { ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '..';
import { ActionRefLabels } from 'further-ui/labels';
import { Column, Row } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';

type Props = {
  onFindAndMatch: () => void;
  matchingReconcilableTransactions: Array<{
    id: string;
    unreconciledAmountTcy: number;
    counterpartyLabel: string;
    actionRef: ActionRef;
    currencyCode: CurrencyCode;
    ledgerDate: CalendarDay;
  }>;
};

const QuickReconcile: React.FC<Props> = ({
  matchingReconcilableTransactions,
  onFindAndMatch,
}) => {
  const { control } = useFormContext<FormValues>();

  if (!matchingReconcilableTransactions.length)
    return (
      <Row centered justify="center" fullWidth>
        <Button
          variant="outlined"
          size="large"
          onClick={() => onFindAndMatch()}
        >
          Find and match
        </Button>
      </Row>
    );

  const options = matchingReconcilableTransactions.map((transaction) => ({
    label: `${
      ActionRefLabels[transaction.actionRef]
    } (${transaction.ledgerDate.toReadable()}) - ${
      transaction.counterpartyLabel
    } - ${numberToCurrencyString(transaction.unreconciledAmountTcy, {
      currency: transaction.currencyCode,
    })}`,
    value: transaction.id,
  }));

  return (
    <Column spacing="sm" fullWidth>
      <Typography variant="h6">Quick reconcile</Typography>
      <Controller
        control={control}
        name="transactionId"
        render={({ field: { onChange, value } }) => (
          <AppAutocomplete
            disableClearable
            options={options}
            fullWidth
            onChange={(_, value) => {
              if (!value) return;
              onChange(value.value);
            }}
            value={options.find((option) => option.value === value)}
            getOptionLabel={(option) => option.label}
            getOptionKey={(option) => option.value}
            placeholder="Select transaction"
          />
        )}
      />
      <Typography variant="body2" color="textSecondary">
        or{' '}
        <TypographyButton
          text="find a match"
          onClick={() => onFindAndMatch()}
        />
      </Typography>
    </Column>
  );
};

export default QuickReconcile;
